<template>
  <div class="modal-mask" :class="{'modal-mask--responsive': modalResponsive, 'modal-mask--flex-container': modalFlexContainer}">
    <div class="modal-wrapper">
      <div class="modal-container" ref="modalContent" tabindex="-1"
           role="dialog" aria-modal="true" :aria-label="ariaLabel || null">
        <div class="modal-header" @click.stop>
          <slot name="header">default header</slot>
          <div class="font-large icon__bg-circle close-cross" @click.stop="$emit('close')"
               role="button" tabindex="0" :aria-label="$t('vue2.a11y.close')"
               @keydown.enter.stop="$emit('close')" @keydown.space.prevent.stop="$emit('close')">
            <span class="icon-cross2 pointer" aria-hidden="true"></span>
          </div>
        </div>
        <div class="modal-body">
          <slot name="body">
            default body
          </slot>
        </div>
        <div class="modal-footer" v-if="isShowFooter">
          <slot name="footer">
            <button class="bttn create-btn right marg-10" @click="$emit('submit')">
              {{ submitTxt }}
            </button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
// Delt stak over åbne modaler, så fx Esc kun lukker den øverste.
const appModalStack = []

export default {
  props: {
    submitTxt: {
      type: String,
      default: 'OK'
    },
    isShowFooter: {
      default: true,
      type: Boolean
    },
    modalResponsive: {
      default: false,
      type: Boolean
    },
    modalFlexContainer: {
      default: false,
      type: Boolean
    },
    // Tilgængeligt navn på dialogen (aria-label) for skærmlæsere.
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {}
  },
  methods: {
    getFocusable () {
      const root = this.$refs.modalContent
      if (!root) return []
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      return Array.from(root.querySelectorAll(selector)).filter(el => {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      })
    },
    // Sætter startfokus ved åbning. Respekterer et inputfelt, der allerede har
    // fået fokus, og foretrækker ellers det første formularfelt.
    focusInitial () {
      const root = this.$refs.modalContent
      if (!root) return
      if (root.contains(document.activeElement) && document.activeElement !== root) {
        return
      }
      const focusable = this.getFocusable()
      const field = focusable.find(el => /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
      const target = field || focusable[0] || root
      target.focus()
    },
    handleKeydown (event) {
      if (appModalStack[appModalStack.length - 1] !== this) return
      if (event.key === 'Escape') {
        this.$emit('close')
        return
      }
      // Fokus-trap (WCAG 2.4.3).
      if (event.key === 'Tab') {
        const root = this.$refs.modalContent
        if (!root) return
        const focusable = this.getFocusable()
        if (focusable.length === 0) {
          event.preventDefault()
          root.focus()
          return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!root.contains(document.activeElement)) {
          event.preventDefault()
          first.focus()
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  },
  mounted () {
    appModalStack.push(this)
    window.addEventListener('keydown', this.handleKeydown)
    this.$nextTick(() => {
      this.focusInitial()
    })
  },
  beforeDestroy () {
    const idx = appModalStack.indexOf(this)
    if (idx > -1) {
      appModalStack.splice(idx, 1)
    }
    window.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>
<style scoped>
@import "../../assets/css/variables.css";

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: rgba(0, 0, 0, .5);
  transition: opacity .15s ease;
  padding-top: 2rem;
}

.modal-wrapper {
  position: relative;
}

@media handheld, only screen and (max-width: 767px) {

  .modal-mask {
    padding-top: 0rem;
  }

  .modal-header {
    font-size: 0.7em;
  }

  .modal-wrapper {
    width: 100%;
    height: 100%;
  }

  .close-cross {
    top: 6px;
    right: 12px;
  }
}

.modal-container {
  transition: all .3s ease;
  height: 100%;
  width: 100%;
  background: #f6f9fc;
  color: #4e5665;
  box-shadow: -1px 1px 5px 0px rgba(50, 50, 50, 0.2);
  position: relative;
  animation-name: fadeUp;
  animation-duration: 0.25s;
}

@media handheld, only screen and (min-width: 767px) {
  .modal-container {
    height: 580px;
    width: 800px;
    border-radius: 3px;
  }

  .modal-mask--flex-container .modal-container {
    width: 900px;
    max-width: 100vw;
  }

  .modal-mask--responsive .modal-container {
    max-height: 100vh;
  }

  .close-cross {
    top: 1rem;
    right: 1rem;
    font-size: 1.5rem;
  }
}

.modal-header {
  background: #fff;
  padding: 1rem 2rem;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  color: #3d4340;
  border-bottom: 1px solid #d8d8d8;
  text-align: left;
  position: relative;
}

.close-cross {
  position: absolute;
}

.close-cross:focus-visible,
.modal-container:focus-visible {
  outline: 2px solid #468AD7;
  outline-offset: 2px;
}

.modal-body {
  height: calc(100% - 100px);
  overflow-y: auto;
}

.modal-mask--flex-container .modal-body {
  padding: 0;
}

.modal-footer {
  background: #f6f9fc;
  overflow: hidden;
  border-top: 1px solid #d8d8d8;
  margin-bottom: 0;
  border-radius: 0 0 4px 4px;
  text-align: center;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  position: absolute;
  bottom: 0;
  right: 0
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  transform: scale(0.8);
}

.create-btn {
  color: white;
  box-shadow: 0 2px 2px rgba(0, 0, 0, .1);
  background-color: #468AD7;
  border: 1px solid #4e80b7 !important;
  transition: background-color 0.3s, transform 0.3s;
  transform: scale(1);
  box-shadow: 0 4px 6px rgb(50 50 93 / 11%), 0 1px 3px rgb(0 0 0 / 8%);
}

.create-btn:hover {
  background-color: #66a1e4;
}

.create-btn:active {
  transform: scale(0.98)
}
</style>



// WEBPACK FOOTER //
// AppModal.vue?60007f46